<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { Select, createListCollection } from '@ark-ui/vue'
import UiFieldContainer from './UiFieldContainer.vue'
import UiIcon from './UiIcon.vue'

export interface UiUnitNumberFieldUnit {
    label: string
    value: string
}

export interface UiUnitNumberFieldProps {
    placeholder?: string
    name?: string
    id?: string
    class?: string | string[] | Record<string, boolean>
    required?: boolean
    readonly?: boolean
    disabled?: boolean
    min?: number
    max?: number
    step?: number
    size?: 'md' | 'lg'
    hint?: string
    error?: string
    invalid?: boolean
    /** Unit options shown in the dropdown. The trigger fits the widest label so it never reflows. */
    units: readonly UiUnitNumberFieldUnit[]
}

const props = withDefaults(defineProps<UiUnitNumberFieldProps>(), {
    class: '',
    required: false,
    readonly: false,
    disabled: false,
    size: 'md',
})

const model = defineModel<string>()
const unitModel = defineModel<string>('unit')

const inputRef = ref<HTMLInputElement>()
const isFocused = ref(false)
const isUnitOpen = ref(false)

const emit = defineEmits<{
    (e: 'validate'): void
}>()

const unitCollection = computed(() =>
    createListCollection({
        items: [...props.units],
        itemToValue: (item: UiUnitNumberFieldUnit) => item.value,
        itemToString: (item: UiUnitNumberFieldUnit) => item.label,
    }),
)

const selectedUnitValues = computed<string[]>({
    get: () => (unitModel.value ? [unitModel.value] : []),
    set: (val) => {
        unitModel.value = val[0]
    },
})

const selectedUnitLabel = computed(
    () =>
        props.units.find((u) => u.value === unitModel.value)?.label
            ?? props.units[0]?.label
            ?? '',
)

function handleInput(event: Event) {
    const target = event.target as HTMLInputElement
    model.value = target.value
}

function focusInput() {
    isFocused.value = true
    inputRef.value?.focus()
}

function blurInput() {
    isFocused.value = false
    inputRef.value?.blur()
    emit('validate')
}

watch(isUnitOpen, (open) => {
    if (open && typeof window !== 'undefined') {
        nextTick(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'))
            })
        })
    }
})
</script>

<template>
    <UiFieldContainer
        :class="props.class"
        :isFocused="isFocused || isUnitOpen"
        :size="props.size"
        :invalid="props.invalid"
        :hint="props.hint"
        :error="props.error"
        :describedByIdBase="props.id || props.name"
        :disabled="props.disabled"
        gap="0"
        @focus="focusInput"
        @blur="blurInput"
    >
        <input
            ref="inputRef"
            :placeholder="props.placeholder"
            :name="props.name"
            :id="props.id || props.name"
            :value="model"
            class="text-md min-w-0 flex-1 se-font-input font-archivo"
            type="number"
            :required="props.required"
            :readonly="props.readonly"
            :disabled="props.disabled"
            :min="props.min"
            :max="props.max"
            :step="props.step"
            :aria-invalid="props.invalid ? 'true' : undefined"
            :aria-describedby="props.error ? (props.id || props.name) + '-error' : (props.hint ? (props.id || props.name) + '-hint' : undefined)"
            @input="handleInput"
            @blur="blurInput"
        />

        <Select.Root
            v-model="selectedUnitValues"
            v-model:open="isUnitOpen"
            :collection="unitCollection"
            :disabled="props.disabled"
            lazyMount
            class="-my-px -mr-4 ml-3 self-stretch"
        >
            <Select.Control class="h-full">
                <Select.Trigger
                    class="flex h-full items-center gap-1 border-l border-se-gray-300 px-3 outline-none transition-colors"
                    :class="props.disabled ? 'cursor-not-allowed text-se-gray-400' : 'cursor-pointer text-se-gray-700 hover:text-se-dark'"
                    @click.stop
                    @mousedown.stop
                >
                    <span class="grid items-center">
                        <span
                            v-for="unit in props.units"
                            :key="`ghost-${unit.value}`"
                            class="invisible col-start-1 row-start-1 whitespace-nowrap se-font-p-small font-archivo"
                            aria-hidden="true"
                        >{{ unit.label }}</span>
                        <span class="col-start-1 row-start-1 whitespace-nowrap se-font-p-small font-archivo">
                            {{ selectedUnitLabel }}
                        </span>
                    </span>
                    <Select.Indicator>
                        <UiIcon
                            name="chevron-down"
                            class="h-3.5 w-3.5 transition-transform duration-150"
                            :class="isUnitOpen ? 'rotate-180' : ''"
                        />
                    </Select.Indicator>
                </Select.Trigger>
            </Select.Control>
            <Teleport to="body" defer>
                <Select.Positioner>
                    <Select.Content
                        class="z-2000 my-1 overflow-hidden rounded-md border border-se-gray-300 bg-se-white py-1 shadow-md min-w-(--reference-width)"
                    >
                        <Select.Item
                            v-for="item in unitCollection.items"
                            :key="item.value"
                            :item="item"
                            class="flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5 hover:bg-se-gray-100 data-highlighted:bg-se-gray-100"
                        >
                            <Select.ItemText class="se-font-p-small font-archivo whitespace-nowrap">{{ item.label }}</Select.ItemText>
                            <Select.ItemIndicator>
                                <UiIcon name="check" class="h-3 w-3 text-se-dark" />
                            </Select.ItemIndicator>
                        </Select.Item>
                    </Select.Content>
                </Select.Positioner>
            </Teleport>
            <Select.HiddenSelect />
        </Select.Root>
    </UiFieldContainer>
</template>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
